import {
  Bot,
  FileText,
  Gift,
  ImageIcon,
  KeyRound,
  LayoutDashboard,
  LineChart,
  LogOut,
  PackageSearch,
  Receipt,
  SettingsIcon,
  ShoppingBag,
  Smartphone,
  Smile,
  Sparkles,
  User,
  Users,
  Users2,
  Zap,
} from "lucide-react";
import type React from "react";
import { Link, useNavigate } from "react-router-dom";

import { AppSidebarNav } from "@/components/AppSidebarNav";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAuthStore } from "@/stores/auth";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const navigate = useNavigate();
  const { user, isAuthenticated, clearAuth } = useAuthStore();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const handleLogout = () => {
    clearAuth();
    navigate("/login");
  };

  // 所有用户都能访问的导航项
  const commonNavItems = [
    {
      title: "商城",
      url: "/mall",
      icon: ShoppingBag,
    },
    {
      title: "Token 充值",
      url: "/topup",
      icon: Gift,
      requiresAuth: true,
    },
    {
      title: "邀请好友",
      url: "/invitation",
      icon: Users2,
      requiresAuth: true,
    },
    {
      title: "API Keys",
      url: "/api-keys",
      icon: KeyRound,
      requiresAuth: true,
    },
    {
      title: "可用模型",
      url: "/available-models",
      icon: Bot,
      requiresAuth: true,
    },
    {
      title: "消费日志",
      url: "/user-logs",
      icon: FileText,
      requiresAuth: true,
    },
  ];

  // 管理员专属导航项（分组）
  const adminNavGroups = isAdmin
    ? [
        {
          title: "商品与订单",
          items: [
            {
              title: "商品管理",
              url: "/admin/products",
              icon: PackageSearch,
            },
            {
              title: "订单号管理",
              url: "/admin/orders",
              icon: Receipt,
            },
          ],
        },
        {
          title: "用户与权限",
          items: [
            {
              title: "用户管理",
              url: "/admin/users",
              icon: Users,
            },
            {
              title: "授权码管理",
              url: "/admin/vip-codes",
              icon: KeyRound,
            },
            {
              title: "兑换码管理",
              url: "/admin/redemption-codes",
              icon: Gift,
            },
            {
              title: "账号批量开通",
              url: "/admin/account-activations",
              icon: Users2,
            },
            {
              title: "财务统计",
              url: "/admin/account-activations/stats",
              icon: LineChart,
            },
            {
              title: "渠道管理",
              url: "/admin/channels",
              icon: Zap,
            },
          ],
        },
        {
          title: "内容管理",
          items: [
            {
              title: "技能商店管理",
              url: "/admin/skills",
              icon: Sparkles,
            },
            {
              title: "表情包管理",
              url: "/admin/stickers",
              icon: Smile,
            },
            {
              title: "邀请页面配置",
              url: "/admin/invitation-page",
              icon: ImageIcon,
            },
          ],
        },
        {
          title: "数据统计",
          items: [
            {
              title: "仪表盘",
              url: "/admin/dashboard",
              icon: LayoutDashboard,
            },
            {
              title: "数据分析",
              url: "/admin/analytics",
              icon: LineChart,
            },
          ],
        },
        {
          title: "应用管理",
          items: [
            {
              title: "APP版本管理",
              url: "/app-version",
              icon: Smartphone,
            },
          ],
        },
      ]
    : [];

  // 过滤需要认证的菜单项
  const filteredCommonNavItems = commonNavItems.filter((item) => {
    if (item.requiresAuth && !isAuthenticated) {
      return false;
    }
    return true;
  });

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:!p-1.5"
            >
              <Link to="/" className="flex items-center gap-2">
                <span className="text-base font-semibold">Alita Planet</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {/* 用户菜单 */}
        {filteredCommonNavItems.length > 0 && (
          <AppSidebarNav items={filteredCommonNavItems} title="用户功能" />
        )}
        {/* 管理员菜单（分组） */}
        {adminNavGroups.map((group) => (
          <AppSidebarNav
            key={group.title}
            items={group.items}
            title={group.title}
          />
        ))}
      </SidebarContent>
      <SidebarFooter>
        {isAuthenticated && user ? (
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton className="w-full">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-blue-500 text-white text-sm">
                        {user.username[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start flex-1 text-left overflow-hidden">
                      <span className="text-sm font-medium truncate w-full">
                        {user.username}
                      </span>
                      <span className="text-xs text-muted-foreground truncate w-full">
                        {user.email}
                      </span>
                    </div>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user.username}</p>
                      <p className="text-xs text-muted-foreground">
                        {user.email}
                      </p>
                      {user.role === "super_admin" && (
                        <p className="text-xs text-red-600 font-medium">
                          超级管理员
                        </p>
                      )}
                      {user.role === "admin" && (
                        <p className="text-xs text-blue-600 font-medium">
                          管理员
                        </p>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")}>
                    <User className="mr-2 h-4 w-4" />
                    个人资料
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <SettingsIcon className="mr-2 h-4 w-4" />
                      管理控制台
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    退出登录
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        ) : (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <Link to="/login" className="w-full touch-target">
                  <User className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="text-sm sm:text-base">登录</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <Link to="/register" className="w-full touch-target">
                  <User className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="text-sm sm:text-base">注册</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
